import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building2, Landmark, LogOut, LayoutDashboard, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ActionEntrepriseCard } from './ActionEntrepriseCard';
import { PlanningHebdoEntreprise } from './PlanningHebdoEntreprise';
import { CandidatsDifficulteDialog } from './CandidatsDifficulteDialog';
import { FicheCandidatEntreprise } from './FicheCandidatEntreprise';
import { SatisfactionPortailPanel } from '../SatisfactionPortailPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PORTAILS = {
  entreprise: { titre: 'Portail Entreprise', icon: Building2, roles: (r) => r?.client || r?.entreprise_finale, placeholder: 'Voir en tant que… (choisir une entreprise)', vide: 'Aucune donnée disponible pour votre entreprise.', videAdmin: 'Sélectionnez une entreprise pour afficher son portail.', aucune: 'Aucune action de formation rattachée à votre entreprise.' },
  financeur: { titre: 'Portail Financeur', icon: Landmark, roles: (r) => r?.financeur, placeholder: 'Voir en tant que… (choisir un financeur)', vide: 'Aucune donnée disponible pour votre organisme.', videAdmin: 'Sélectionnez un financeur pour afficher son portail.', aucune: 'Aucune action de formation financée par votre organisme.' },
};

// Page commune aux portails Entreprise et Financeur (même présentation ; le financeur voit l'entreprise finale de chaque candidat)
export const PortailEntrepriseBase = ({ mode }) => {
  const cfg = PORTAILS[mode];
  const apiBase = `${API_URL}/portail/${mode}`;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [societes, setSocietes] = useState([]);
  const [viewAs, setViewAs] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fiche, setFiche] = useState(null);
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    if (isAdmin) axios.get(`${API_URL}/societes`).then(r => setSocietes(r.data.filter(s => cfg.roles(s.roles)).map(s => ({ id: s.client_id, nom: s.nom })).sort((a, b) => a.nom.localeCompare(b.nom)))).catch(() => {});
  }, [isAdmin, cfg]);

  useEffect(() => {
    if (isAdmin && !viewAs) { setData(null); setLoading(false); return; }
    setLoading(true);
    axios.get(`${apiBase}/dashboard`, { params: viewAs ? { view_as: viewAs } : {} })
      .then(r => setData(r.data))
      .catch(e => {
        if (e.response?.status === 403 && !isAdmin) { navigate(mode === 'financeur' ? '/portail-client' : '/portail-financeur', { replace: true }); return; }
        toast.error(e.response?.data?.detail || 'Erreur lors du chargement'); setData(null);
      })
      .finally(() => setLoading(false));
  }, [viewAs, isAdmin, apiBase, mode, navigate]);

  const alertes = useMemo(() => (data?.actions || []).flatMap(a => a.candidats.filter(c => c.en_difficulte || c.nb_alertes_formateur > 0 || c.abandon).map(c => ({ ...c, dossier_code: a.code, dossier_id: a.dossier_id }))), [data]);
  const Icon = cfg.icon;
  const showEntreprise = mode === 'financeur';

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`portail-${mode}-page`}>
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center"><Icon className="w-5 h-5 text-cyan-300" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300" data-testid="portail-titre">{cfg.titre}</p>
              <p className="text-lg font-semibold font-['Plus_Jakarta_Sans'] leading-tight" data-testid="entreprise-nom">{data?.client?.nom || (isAdmin ? 'Vue administrateur' : `${user?.prenom || ''} ${user?.nom || ''}`)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isAdmin && mode === 'entreprise' && data?.client?.portails?.includes('financeur') && (
              <Button variant="outline" size="sm" className="bg-transparent border-cyan-500/50 text-cyan-200 hover:bg-slate-800 hover:text-white" onClick={() => navigate(mode === 'financeur' ? '/portail-client' : '/portail-financeur')} data-testid="switch-portail-btn">
                {mode === 'financeur' ? <><Building2 className="w-4 h-4 mr-2" /> Portail Entreprise</> : <><Landmark className="w-4 h-4 mr-2" /> Portail Financeur</>}
              </Button>
            )}
            {isAdmin && (
              <Select value={viewAs} onValueChange={setViewAs}>
                <SelectTrigger className="w-72 bg-slate-800 border-slate-700 text-white" data-testid="entreprise-view-as-select"><SelectValue placeholder={cfg.placeholder} /></SelectTrigger>
                <SelectContent>{societes.map(c => <SelectItem key={c.id} value={c.id} data-testid={`entreprise-option-${c.id}`}>{c.nom}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {isAdmin ? (
              <Button variant="outline" size="sm" className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="client-back-admin-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Retour admin</Button>
            ) : (
              <Button variant="outline" size="sm" className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white" onClick={() => { logout(); navigate('/login'); }} data-testid="client-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Déconnexion</Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>
        ) : !data ? (
          <div className="text-center py-20 text-slate-500" data-testid="entreprise-empty-state">{isAdmin ? cfg.videAdmin : cfg.vide}</div>
        ) : (
          <>
            <PlanningHebdoEntreprise actions={data.actions} />
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><LayoutDashboard className="w-4 h-4 text-cyan-600" /> Actions de formation <span className="text-xs font-normal text-slate-400">({data.actions.length})</span></h2>
              {alertes.length > 0 && (
                <button type="button" onClick={() => setShowDiff(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1 hover:bg-red-100" data-testid="stat-difficulte">
                  <AlertTriangle className="w-3.5 h-3.5" /> {alertes.length} candidat{alertes.length > 1 ? 's' : ''} en difficulté · <span className="underline underline-offset-2" data-testid="stat-difficulte-link">Voir la liste</span>
                </button>
              )}
            </div>
            <div className="space-y-4" data-testid="entreprise-actions-list">
              {data.actions.length === 0 && <p className="text-slate-500 text-sm">{cfg.aucune}</p>}
              {data.actions.map(a => (
                <div key={a.dossier_id} className="space-y-2">
                  <ActionEntrepriseCard action={a} apiBase={apiBase} viewAs={viewAs || undefined} showEntreprise={showEntreprise} onOpenCandidat={(cid) => setFiche({ candidat_id: cid, dossier_id: a.dossier_id })} />
                  <SatisfactionPortailPanel portail={mode} dossierId={a.dossier_id} viewAs={viewAs || undefined} />
                </div>
              ))}
            </div>
            <CandidatsDifficulteDialog open={showDiff} onClose={() => setShowDiff(false)} alertes={alertes} onOpenCandidat={(cid, did) => setFiche({ candidat_id: cid, dossier_id: did })} />
          </>
        )}
      </main>
      <FicheCandidatEntreprise open={!!fiche} apiBase={apiBase} candidatId={fiche?.candidat_id} dossierId={fiche?.dossier_id} viewAs={viewAs} onClose={() => setFiche(null)} />
    </div>
  );
};
